/**
 * TypeToConfirm — a confirmation you cannot give by reflex.
 *
 * For the actions that are not quite destructive but are worse than a mistake:
 * revoking a person's access, emptying something, anything where the cost of a
 * mis-click is somebody else's afternoon. A Cancel/Confirm pair is one button
 * press away from an accident; typing the name is a deliberate act, and it also
 * forces the admin to READ which record they are about to act on.
 *
 * There was no such dialog in the app before this — the destructive
 * confirmations are all plain two-button affairs (and a comment in `Matters`
 * claimed otherwise, which is corrected). It is shared rather than local to
 * admin because the second caller always arrives.
 *
 * IT IS A MODAL BODY, NOT A MODAL. `GlobalModal`'s `custom` type draws the
 * glass panel, the icon and the headline; this is what goes inside it, so it
 * paints no surface of its own. Raise it with:
 *
 *   dispatch(showModal({ type: 'custom', message: t('Revoke access'),
 *     icon: <PiProhibitLight … />, content: <TypeToConfirm … /> }))
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from './TailwindComponents/Button';
import Input from './TailwindComponents/Input';
import { Well } from './brand/Field';

export interface TypeToConfirmProps {
  /**
   * What must be typed, verbatim — a person's name, a matter's title.
   *
   * Compared case-insensitively and trimmed: the point is deliberate intent,
   * not transcription accuracy, and failing someone for a trailing space
   * teaches them to paste rather than to read.
   */
  phrase: string;
  /** Label above the field. Defaults to naming the phrase. */
  inputLabel?: string;
  /** The sentence that says what is about to happen. */
  description: React.ReactNode;
  /**
   * The specific consequences, one per line. A paragraph gets skimmed; a short
   * list gets read, and each line here is a thing the admin cannot undo by
   * closing the dialog.
   */
  consequences?: React.ReactNode[];
  /** Shown last, quieter — e.g. that the action is reversible. */
  reassurance?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** `destructive` by default; `primary` for a reversible action. */
  confirmVariant?: 'destructive' | 'primary';
  /** May be async — the button shows a pending state until it settles. */
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}

const TypeToConfirm: React.FC<TypeToConfirmProps> = ({
  phrase,
  inputLabel,
  description,
  consequences,
  reassurance,
  confirmLabel,
  cancelLabel,
  confirmVariant = 'destructive',
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const [pending, setPending] = useState(false);

  const matches = useMemo(
    () => typed.trim().toLowerCase() === (phrase || '').trim().toLowerCase() && !!phrase.trim(),
    [typed, phrase],
  );

  const handleConfirm = async () => {
    if (!matches || pending) return;
    setPending(true);
    try {
      await onConfirm();
    } finally {
      // The caller closes the modal on success; if it threw, the dialog stays
      // open with what was typed intact rather than making them start again.
      setPending(false);
    }
  };

  return (
    <div className="w-[min(30rem,84vw)] space-y-4">
      <div className="space-y-3 px-4">
        <p className="text-sm text-fg-body">{description}</p>

        {!!consequences?.length && (
          <Well className="space-y-1.5">
            {consequences.map((line, i) => (
              <div key={i} className="flex gap-2">
                <span aria-hidden className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-champagne" />
                <span className="text-xs leading-relaxed text-fg-body">{line}</span>
              </div>
            ))}
          </Well>
        )}

        {reassurance && <p className="text-xs text-fg-muted">{reassurance}</p>}

        <div>
          <Input
            id="type-to-confirm"
            type="text"
            label={inputLabel || t('Type the name to confirm')}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={phrase}
            autoComplete="off"
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' && matches) {
                e.preventDefault();
                void handleConfirm();
              }
            }}
          />
          {/* The phrase, selectable, so nobody has to squint at the placeholder
              — and shown as a quiet hint rather than an error, because not
              having typed it yet is not a mistake. */}
          <p className="mt-1 text-[11px] text-fg-muted">
            {t('Type')} <span className="font-medium text-fg-body">{phrase}</span>
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-hairline px-4 pt-4">
        <Button variant="neutral" size="small" onClick={onCancel} disabled={pending}>
          {cancelLabel || t('Cancel')}
        </Button>
        <Button
          variant={confirmVariant}
          size="small"
          onClick={handleConfirm}
          disabled={!matches}
          isLoading={pending}
          data-testid="type-to-confirm-submit"
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
};

export default TypeToConfirm;
